import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { CampaignFileUploadService } from '../../../modules/admin/campaign/services/campaign-file-upload.service';
import { IUploadProgress } from '../../../modules/admin/campaign/models/campaign-file.types';

@Component({
    selector: 'upload-indicator',
    templateUrl: './upload-indicator.component.html',
    styleUrls: ['./upload-indicator.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'uploadIndicator',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressBarModule
    ],
})
export class UploadIndicatorComponent implements OnInit, OnDestroy {
    @ViewChild('uploadsOrigin') private _uploadsOrigin: MatButton;
    @ViewChild('uploadsPanel') private _uploadsPanel: TemplateRef<any>;

    uploads: IUploadProgress[] = [];
    activeCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _uploadService: CampaignFileUploadService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _toastr: ToastrService,
        private _transloco: TranslocoService,
    ) {}

    ngOnInit(): void {
        // Subscribe to upload changes
        this._uploadService.uploads$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((uploadsMap: Map<string, IUploadProgress>) => {
                // Convert map to array
                this.uploads = Array.from(uploadsMap.values());

                // Calculate active uploads count
                this.activeCount = this.uploads.filter(
                    u => u.status === 'uploading' || u.status === 'pending'
                ).length;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to upload complete events - show toast notification
        this._uploadService.uploadComplete$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ filename }) => {
                this._toastr.success(
                    this._transloco.translate('File uploaded successfully', { filename }),
                    this._transloco.translate('Upload Complete')
                );
            });

        // Subscribe to upload error events - show error toast
        this._uploadService.uploadError$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ error }) => {
                this._toastr.error(
                    error,
                    this._transloco.translate('Upload Error')
                );
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    openPanel(): void {
        if (!this._uploadsPanel || !this._uploadsOrigin) {
            return;
        }

        if (!this._overlayRef) {
            this._createOverlay();
        }

        this._overlayRef.attach(new TemplatePortal(this._uploadsPanel, this._viewContainerRef));
    }

    closePanel(): void {
        this._overlayRef?.detach();
    }

    cancelUpload(upload: IUploadProgress): void {
        this._uploadService.cancelUpload(upload.fileId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this._uploadService.removeUploadFromState(upload.fileId);
                }
            });
    }

    formatFileSize(bytes: number): string {
        return this._uploadService.formatFileSize(bytes);
    }

    getStatusIcon(status: string): string {
        switch (status) {
            case 'completed': return 'heroicons_solid:check-circle';
            case 'failed': return 'heroicons_solid:x-circle';
            case 'cancelled': return 'heroicons_solid:minus-circle';
            case 'uploading': return 'heroicons_solid:arrow-up-circle';
            default: return 'heroicons_solid:clock';
        }
    }

    getStatusColor(status: string): string {
        switch (status) {
            case 'completed': return 'text-green-500';
            case 'failed': return 'text-red-500';
            case 'cancelled': return 'text-gray-400';
            case 'uploading': return 'text-blue-500';
            default: return 'text-gray-400';
        }
    }

    trackByFn(index: number, item: IUploadProgress): string {
        return item.fileId;
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._uploadsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
